import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { 
    XMarkIcon, 
    PlayIcon, 
    PauseIcon, 
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    DocumentArrowDownIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getRecordingDownloadUrl } from '../../Requests/VideoConferenceRequests';

const ViewRecordingModal = ({ conference, recordings, handleClose }) => {
    const { t } = useTranslation();
    const [selectedRecording, setSelectedRecording] = useState(null);
    const [presignedUrl, setPresignedUrl] = useState(null);
    const [loadingUrl, setLoadingUrl] = useState(false);
    const [urlError, setUrlError] = useState(null);
    const [urlExpiresAt, setUrlExpiresAt] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [videoElement, setVideoElement] = useState(null);
    const [isComponentMounted, setIsComponentMounted] = useState(true);
    const videoRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Find the composite recording - debounced to prevent rapid changes
    useEffect(() => {
        if (!isComponentMounted) return;
        
        const timer = setTimeout(() => {
            const compositeRecording = recordings.find(r => r.type === 'composite' && r.status === 'completed');
            if (compositeRecording && compositeRecording.id !== selectedRecording?.id) {
                setSelectedRecording(compositeRecording);
            } else if (!selectedRecording && recordings.length > 0) {
                const firstRecording = recordings.find(r => r.status === 'completed');
                if (firstRecording) {
                    setSelectedRecording(firstRecording);
                }
            }
        }, 100); // Small debounce to prevent rapid changes
        
        return () => clearTimeout(timer);
    }, [recordings.length, selectedRecording?.id, isComponentMounted]);

    // Reset player states when recording changes - but avoid interrupting loads
    useEffect(() => {
        if (selectedRecording?.id && !loadingUrl && isComponentMounted) {
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
            setIsMuted(false);
            setIsFullscreen(false);
            setUrlError(null);
            
            // Only reset video element if it's not currently loading
            if (videoElement && videoElement.readyState >= 1) {
                videoElement.pause();
                videoElement.currentTime = 0;
            }
        }
    }, [selectedRecording?.id, loadingUrl, isComponentMounted]);

    // Fetch presigned URL when recording is selected - debounced
    useEffect(() => {
        if (selectedRecording?.id && isComponentMounted) {
            // Cancel any existing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            const timer = setTimeout(() => {
                if (isComponentMounted) {
                    fetchPresignedUrl(selectedRecording.id);
                }
            }, 150); // Debounce to prevent rapid fetching
            
            return () => clearTimeout(timer);
        }
    }, [selectedRecording?.id, isComponentMounted]);

    // Defined before the effects that depend on it — referencing this callback
    // in a useEffect dependency array before its initialization throws a
    // temporal-dead-zone ReferenceError and crashes the modal (blank video).
    const fetchPresignedUrl = useCallback(async (recordingId) => {
        if (!isComponentMounted) return;

        try {
            setLoadingUrl(true);
            setUrlError(null);
            setPresignedUrl(null);

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            const response = await getRecordingDownloadUrl(recordingId);

            // Check if component is still mounted before setting state
            if (isComponentMounted) {
                setPresignedUrl(response.data.download_url);
                setUrlExpiresAt(response.data.expires_at);
            }

        } catch (error) {
            if (error.name !== 'AbortError' && isComponentMounted) {
                console.error('Error fetching presigned URL:', error);
                setUrlError(error.response?.data?.error || t('VideoConference.Recording.FailedToLoadVideo'));
            }
        } finally {
            if (isComponentMounted) {
                setLoadingUrl(false);
            }
        }
    }, [isComponentMounted]);

    // Handle URL expiration - refresh URL when it's about to expire
    useEffect(() => {
        if (urlExpiresAt && selectedRecording?.id && isComponentMounted) {
            const expirationTime = new Date(urlExpiresAt).getTime();
            const currentTime = Date.now();
            const timeUntilExpiration = expirationTime - currentTime;

            // Refresh URL 5 minutes before expiration (300,000 ms)
            const refreshTime = Math.max(timeUntilExpiration - 300000, 60000); // At least 1 minute

            if (refreshTime > 0) {
                const timer = setTimeout(() => {
                    if (isComponentMounted && selectedRecording?.id) {
                        fetchPresignedUrl(selectedRecording.id);
                    }
                }, refreshTime);

                return () => clearTimeout(timer);
            }
        }
    }, [urlExpiresAt, selectedRecording?.id, isComponentMounted, fetchPresignedUrl]);

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePlayPause = () => {
        if (videoElement) {
            if (isPlaying) {
                videoElement.pause();
            } else {
                videoElement.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleMuteToggle = () => {
        if (videoElement) {
            videoElement.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const handleFullscreen = () => {
        if (videoElement) {
            if (!isFullscreen) {
                if (videoElement.requestFullscreen) {
                    videoElement.requestFullscreen();
                } else if (videoElement.webkitRequestFullscreen) {
                    videoElement.webkitRequestFullscreen();
                } else if (videoElement.msRequestFullscreen) {
                    videoElement.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }
    };

    const handleTimeUpdate = (e) => {
        setCurrentTime(e.target.currentTime);
    };

    const handleLoadedMetadata = (e) => {
        setDuration(e.target.duration);
    };

    const handleSeek = (e) => {
        const seekTime = (e.target.value / 100) * duration;
        if (videoElement) {
            videoElement.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const handleDownload = () => {
        if (presignedUrl) {
            const link = document.createElement('a');
            link.href = presignedUrl;
            link.download = selectedRecording?.display_name || selectedRecording?.filename || 'recording.mp4';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (selectedRecording?.id) {
            // If no presigned URL yet, try to fetch it
            fetchPresignedUrl(selectedRecording.id).then(() => {
                if (presignedUrl) {
                    handleDownload();
                }
            });
        }
    };

    // Component mount tracking and cleanup
    useEffect(() => {
        setIsComponentMounted(true);
        
        return () => {
            setIsComponentMounted(false);
            
            // Cancel any ongoing requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // Cleanup video element
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.src = '';
                videoRef.current.load();
            }
        };
    }, []);
    
    // Handle fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (isComponentMounted) {
                setIsFullscreen(!!document.fullscreenElement);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, [isComponentMounted]);

    if (!conference || !selectedRecording) {
        return null;
    }

    return (
        <Dialog open={true} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/75" aria-hidden="true" />
            
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('VideoConference.Recording.ConferenceRecording')}
                            </Dialog.Title>
                            <p className="text-sm text-gray-600 mt-1">
                                {conference.title}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleDownload}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                                title={t('VideoConference.Recording.DownloadRecording')}
                            >
                                <DocumentArrowDownIcon className="h-5 w-5" />
                            </button>
                            <button
                                onClick={handleClose}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Video Player */}
                    <div className="flex-1 p-6 flex flex-col">
                        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
                            {loadingUrl ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="text-white text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                                        <p>{t('VideoConference.Recording.LoadingRecording')}</p>
                                    </div>
                                </div>
                            ) : urlError ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="text-red-400 text-center">
                                        <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4" />
                                        <p className="text-lg font-medium mb-2">{t('VideoConference.Recording.ErrorLoadingRecording')}</p>
                                        <p className="text-sm">{urlError}</p>
                                        <button
                                            onClick={() => selectedRecording?.id && fetchPresignedUrl(selectedRecording.id)}
                                            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm"
                                        >
                                            {t('VideoConference.Recording.TryAgain')}
                                        </button>
                                    </div>
                                </div>
                            ) : presignedUrl ? (
                                <video
                                    ref={(el) => {
                                        videoRef.current = el;
                                        setVideoElement(el);
                                    }}
                                    className="w-full h-auto max-h-[60vh]"
                                    src={presignedUrl}
                                    onTimeUpdate={handleTimeUpdate}
                                    onLoadedMetadata={handleLoadedMetadata}
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    onError={(e) => {
                                        if (isComponentMounted) {
                                            setUrlError(t('VideoConference.Recording.FailedToLoadVideo'));
                                        }
                                    }}
                                    controls={false}
                                    preload="metadata"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <div className="flex items-center justify-center h-64">
                                    <div className="text-gray-400 text-center">
                                        <p>{t('VideoConference.Recording.NoRecordingUrl')}</p>
                                    </div>
                                </div>
                            )}

                            {/* Custom Controls Overlay - only show when video is loaded */}
                            {presignedUrl && !loadingUrl && !urlError && (
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-4">
                                {/* Progress Bar */}
                                <div className="mb-3">
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={duration ? (currentTime / duration) * 100 : 0}
                                        onChange={handleSeek}
                                        className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                                        style={{
                                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${duration ? (currentTime / duration) * 100 : 0}%, #4b5563 ${duration ? (currentTime / duration) * 100 : 0}%, #4b5563 100%)`
                                        }}
                                    />
                                </div>

                                {/* Control Buttons */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={handlePlayPause}
                                            className="text-white hover:text-blue-400 p-2 rounded-full hover:bg-black/50"
                                        >
                                            {isPlaying ? (
                                                <PauseIcon className="h-6 w-6" />
                                            ) : (
                                                <PlayIcon className="h-6 w-6" />
                                            )}
                                        </button>

                                        <button
                                            onClick={handleMuteToggle}
                                            className="text-white hover:text-blue-400 p-2 rounded-full hover:bg-black/50"
                                        >
                                            {isMuted ? (
                                                <SpeakerXMarkIcon className="h-5 w-5" />
                                            ) : (
                                                <SpeakerWaveIcon className="h-5 w-5" />
                                            )}
                                        </button>

                                        <div className="text-white text-sm">
                                            {formatDuration(currentTime)} / {formatDuration(duration)}
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleFullscreen}
                                        className="text-white hover:text-blue-400 p-2 rounded-full hover:bg-black/50"
                                    >
                                        {isFullscreen ? (
                                            <ArrowsPointingInIcon className="h-5 w-5" />
                                        ) : (
                                            <ArrowsPointingOutIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>

                        {/* Recording Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500">Type:</span>
                                    <span className="ml-2 text-gray-900 capitalize">{selectedRecording.type}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Status:</span>
                                    <span className="ml-2 text-gray-900 capitalize">{selectedRecording.status}</span>
                                </div>
                                {selectedRecording.participant_display_name && (
                                    <div>
                                        <span className="text-gray-500">Participant:</span>
                                        <span className="ml-2 text-gray-900">{selectedRecording.participant_display_name}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Additional Recordings */}
                        {recordings.length > 1 && (
                            <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Other Recordings</h4>
                                <div className="space-y-2">
                                    {recordings
                                        .filter(r => r.id !== selectedRecording.id)
                                        .map(recording => (
                                            <div
                                                key={recording.id}
                                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm"
                                            >
                                                <div>
                                                    <span className="font-medium text-gray-900">
                                                        {recording.display_name || `${recording.type} Recording`}
                                                    </span>
                                                    {recording.participant_display_name && (
                                                        <span className="ml-2 text-gray-600 text-xs">
                                                            • {recording.participant_display_name}
                                                        </span>
                                                    )}
                                                    <span className="ml-2 text-gray-500">
                                                        ({recording.status})
                                                    </span>
                                                </div>
                                                {recording.status === 'completed' && (
                                                    <button
                                                        onClick={() => {
                                                            if (isComponentMounted && !loadingUrl) {
                                                                // Cancel current request if any
                                                                if (abortControllerRef.current) {
                                                                    abortControllerRef.current.abort();
                                                                }
                                                                // Set new recording
                                                                setSelectedRecording(recording);
                                                            }
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                                        disabled={loadingUrl}
                                                    >
                                                        {loadingUrl ? t('VideoConference.Recording.LoadingButton') : t('VideoConference.Recording.ViewButton')}
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default ViewRecordingModal;